export enum EnvEnum {
    PRODUCTION = 'production',
    STAGING = 'staging',
    DEVELOPMENT = 'development',
    PREPROD = 'preprod',
    TESTING = 'testing',
    LOCAL = 'local',
    PREVIEW = 'preview'
}
export const ENVIRONMENT: EnvEnum = (import.meta.env.VITE_REACT_APP_ENV as EnvEnum) ?? EnvEnum.LOCAL

export const TMX_ORG_ID = import.meta.env.VITE_TMX_ORG_ID

export const FLINKS_IFRAME_SRC = import.meta.env.VITE_FLINKS_IFRAME_SRC
/** Alterna-specific Flinks entry; falls back to main iframe URL if unset. */
export const ALTERNA_FLINKS_IFRAME_SRC = import.meta.env.VITE_ALTERNA_FLINKS_IFRAME_SRC ?? FLINKS_IFRAME_SRC
export const IS_PRODUCTION = ENVIRONMENT === 'production'

export const API_URL = IS_PRODUCTION ? import.meta.env.VITE_PUBLIC_API_URL : import.meta.env.VITE_PREQUAL_API_URL

export const ROUTES = {
    PREQUAL: '/prequal',
    PERSONAL_LOAN: '/personal-loan',
    PORTAL: '/portal'
}

export const PREQUAL_ROUTES = {
    ROOT: 'prequal',
    LOAN_AMOUNT: 'loan-amount',
    ABOUT_YOURSELF: 'about-yourself',
    PERSONAL_INFO: 'personal-info',
    EMPLOYMENT_INFO: 'employment-info',
    FINANCIAL_INFO: 'financial-info',
    HOUSING_STATUS: 'housing-status',
    OFFER: 'offer/:applicationId',
    DECLINED: 'declined',
    ERROR: 'error'
}

export const OFFER_ROUTES = {
    INDEX: 'offer',
    AFFILIATE_OFFER: 'affiliate-offer',
    EMBEDDED_OFFER: 'embedded-offer',
    FIG_TO_KIZZO_OFFER: 'fig-to-kizzo-offer',
    KIZZO_TO_FIG_OFFER: 'kizzo-to-fig-offer',
    KIZZO_TO_KIZZO_OFFER: 'kizzo-offer'
}

export const PERSONAL_LOAN_ROUTES = {
    ROOT: 'personal-loan',
    CUSTOMIZE_YOUR_OFFER: 'customize-your-offer',
    PRIME_TRUST: 'prime-trust',
    CONFIRM_PAYMENT_SCHEDULE: 'confirm-payment-schedule',
    EDIT_PAYMENT_SCHEDULE: 'edit-payment-schedule',
    KYC_INTRO: 'kyc-intro',
    KYC_VERIFY: 'kyc-verify',
    KYC_VERIFY_OLD: 'kyc-verify-old',
    KYC_GENERIC_RETRY: 'kyc-generic-retry',
    KYC_IMAGE_RETRY: 'kyc-image-retry',
    KYC_PII_RETRY: 'kyc-pii-retry',
    KYC_PERMISSIONS_SCREEN: 'kyc-permissions-screen',
    FLINKS_CONNECT: 'flinks-connect',
    FLINKS_CONNECT_CHANGE: 'flinks-connect-change',
    FLINKS_STATUS_CHECK: 'flinks-status-check',
    FLINKS_BANK_INVALID: 'flinks-bank-invalid',
    FLINKS_PAYMENT_CHECK: 'flinks-payment-check',
    CONNECT_BANK_ACCOUNT: 'connect-bank-account',
    PAYMENT_PROTECTION: 'payment-protection',
    REVIEW: 'review',
    FINISH: 'finish',
    PENDING: 'pending',
    DECLINED: 'declined',
    EXPIRED: 'expired',
    ERROR: 'error',
    EMBEDDED_EMPLOYMENT_STATUS: 'employment-status',
    EMBEDDED_PHONE_NUMBER_STEP: 'phone-number'
}

export const PORTAL_ROUTES = {
    ROOT: 'portal',
    SUMMARY: 'summary',
    PROFILE: 'profile',
    DOCUMENTS: 'documents',
    PAYMENT_PROTECTION: 'payment-protection',
    CHANGE_BANK_ACCOUNT: 'bank-account/update',
    PAYMENTS: 'payments',
    CREDIT_RENEWAL: 'credit-renewal',
    EMAIL_CHANGED: 'email-changed',
    ERROR: 'error'
}

export const MAX_LOAN_AMOUNT = 35000
export const MIN_LOAN_AMOUNT = 2000
export const MAX_LOAN_TERM_FOR_APR_OVER_21_99 = 60
export const MAX_LOAN_TERM_IN_MONTHS = 84
export const MIN_LOAN_TERM_IN_MONTHS = 24
export const LOAN_TERM_OPTIONS = [24, 36, 48, 60, 72, 84]
export const LOAN_PURPOSES = [
    'DEBT_CONSOLIDATION',
    'EXPENSES',
    'HOME_IMPROVEMENT',
    'EMERGENCY',
    'AUTOMOBILE_FINANCING_OR_REPAIR',
    'EDUCATION',
    'PERSONAL_INVESTMENT',
    'OTHER'
] as const
export const EMPLOYMENT_STATUSES = [
    'FULL_TIME_EMPLOYED',
    'PART_TIME_EMPLOYED',
    'SEASONAL_EMPLOYED',
    'SELF_EMPLOYED',
    'UNEMPLOYED',
    'RETIRED',
    'STUDENT',
    'DISABILITY_OR_LONG_TERM_LEAVE'
] as const

// Employment statuses that require additional employment information
export const EMPLOYMENT_STATUSES_REQUIRING_INFO = ['FULL_TIME_EMPLOYED', 'PART_TIME_EMPLOYED', 'SEASONAL_EMPLOYED', 'SELF_EMPLOYED'] as const

/** Financial-info step: vehicle ownership (API + form values). */
export const VEHICLE_OWNERSHIP = {
    FULLY_PAID: 'FULLY_PAID',
    LEASING: 'LEASING',
    ACTIVE_AUTO_LOAN: 'ACTIVE_AUTO_LOAN',
    NONE: 'NONE'
} as const

export const VEHICLE_OWNERSHIP_VALUES = [
    VEHICLE_OWNERSHIP.FULLY_PAID,
    VEHICLE_OWNERSHIP.LEASING,
    VEHICLE_OWNERSHIP.ACTIVE_AUTO_LOAN,
    VEHICLE_OWNERSHIP.NONE
] as const

export const EMPLOYMENT_STATUSES_REQUIRING_ANNUAL_INCOME_WITHOUT_INFO_FORM = ['UNEMPLOYED', 'RETIRED', 'STUDENT', 'DISABILITY_OR_LONG_TERM_LEAVE'] as const

export const provincesList = [
    { value: 'AB', label: 'Alberta' },
    { value: 'BC', label: 'British Columbia' },
    { value: 'MB', label: 'Manitoba' },
    { value: 'NB', label: 'New Brunswick' },
    { value: 'NL', label: 'Newfoundland and Labrador' },
    { value: 'NS', label: 'Nova Scotia' },
    { value: 'NT', label: 'Northwest Territories' },
    { value: 'NU', label: 'Nunavut' },
    { value: 'ON', label: 'Ontario' },
    { value: 'PE', label: 'Prince Edward Island' },
    { value: 'QC', label: 'Quebec' },
    { value: 'SK', label: 'Saskatchewan' },
    { value: 'YT', label: 'Yukon' }
].sort((a, b) => a.label.localeCompare(b.label))

export const QC_PROVINCE = 'QC'
export type LoanPurpose = (typeof LOAN_PURPOSES)[number]
export const RESIDENCY_STATUSES = ['CITIZEN', 'PERMANENT_RESIDENT', 'WORK_PERMIT', 'INTERNATIONAL_STUDENT', 'OTHER'] as const
export const LEVEL_OF_EDUCATION = ['HIGH_SCHOOL_OR_LESS', 'SOME_COLLEGE', 'BACHELORS', 'GRADUATE', 'OTHER'] as const

export const HOUSE_TYPES = {
    RENTED: 'RENTED',
    OWNED: 'OWNED',
    LIVING_WITH_FAMILY_OR_FRIENDS: 'LIVING_WITH_FAMILY_OR_FRIENDS'
} as const

// PostHog event names live in app/tracking/events.ts. Re-exported here so existing
// `import … from 'app/utils/constants'` call sites keep working.
// Use a relative path (not `app/tracking/events`): routes.ts imports this file during
// the React Router build step, before Vite's `app/*` path alias is available.
export {
    ANALYTICS_EVENTS_KEY,
    ANALYTICS_OBJECTS,
    ANALYTICS_EVENTS,
    FUNNEL_EVENTS,
    FUNNEL_EVENT_PROPERTY_KEYS,
    OTP_FAILURE_REASONS,
    SCREEN_NAMES
} from '../tracking/events'

export enum PaymentProtectionEnum {
    PROTECTION_ACCEPTED = 'protection_accepted',
    PROTECTION_DECLINED = 'protection_declined',
    PROTECTION_NOT_SELECTED = 'not_selected',
    PROTECTION_NOT_AVAILABLE = 'not_available'
}

export const emailValidationRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const OFFER_STATUS = {
    PRE_AUTHORIZED: 'PRE_AUTHORIZED',
    PENDING: 'PENDING',
    DECLINED: 'DECLINED',
    SETTLED: 'SETTLED',
    AUTHORIZED: 'AUTHORIZED',
    APPROVED: 'APPROVED',
    EXPIRED: 'EXPIRED'
}

export const KYC_REVIEW_REASON = {
    PII_MISMATCH: 'pii_mismatch',
    IMAGE_QUALITY: 'image_quality',
    SUPPORTED_DOCUMENT: 'supported_document',
    OTHER: 'other'
}

export const KYC_STATUS = {
    REVIEW: 'review',
    APPROVED: 'approved',
    DECLINED: 'declined'
}

export const KYC_PII_MISMATCH_REASON = {
    TU_AND_ONFIDO_NAME_EQUAL: 'TU_AND_ONFIDO_NAME_EQUAL',
    TU_AND_ONFIDO_DOB_EQUAL: 'TU_AND_ONFIDO_DOB_EQUAL',
    TU_AND_ONFIDO_NAME_AND_DOB_EQUAL: 'TU_AND_ONFIDO_NAME_AND_DOB_EQUAL',
    TU_AND_ONFIDO_NOT_EQUAL: 'TU_AND_ONFIDO_NOT_EQUAL'
}
export const MINIMUM_ANNUAL_INCOME = 10000
/** Income above this threshold requires explicit user confirmation on the employment step. */
export const HIGH_INCOME_ALERT_THRESHOLD = 300000

export enum PrequalType {
    EMBEDDED = 'EMBEDDED',
    AFFILIATE = 'AFFILIATE'
}

export const FRESHCHAT_TOKEN = '2af4563a-4787-4613-9a3f-63341af13559'
export const FRESHCHAT_HOST = 'https://fig-help.freshchat.com'
export const FRESHCHAT_WIDGET_UUID = '158975d8-8dcb-48c8-8969-0ad2c20d54c3'
export const FRESHCHAT_CHANNEL_NAME = 'Fig Live Chat'
export const FRESHCHAT_SCRIPT_URL = 'https://fig-help.freshchat.com/js/widget.js'
export const SENTRY_RELEASE = import.meta.env.VITE_APP_SENTRY_RELEASE || 'local-dev'
export const SENTRY_DSN = import.meta.env.VITE_APP_SENTRY_DSN
export const BRAZE_API_KEY = import.meta.env.VITE_BRAZE_API_KEY
export const BRAZE_BASE_URL = import.meta.env.VITE_BRAZE_BASE_URL

export enum ApplicationStatus {
    PRE_AUTHORIZED = 'PRE_AUTHORIZED',
    AUTHORIZED = 'AUTHORIZED',
    SETTLED = 'SETTLED',
    APPROVED = 'APPROVED',
    DECLINED = 'DECLINED'
}

// Monthly payment date max # of days the user can select in the future when editing payment schedule (this is a requirement set by Q2's API docs)
// ref: https://www.notion.so/fig-financial/Repayment-Schedule-API-V3-Solution-Doc-24976c6fcc1b80e08a52d1dc11a26220?source=copy_link#24976c6fcc1b8095a93dd259bce19c5c
export const MAX_MONTHLY_PAYMENT_DATE_ROC = 37
export const MAX_MONTHLY_PAYMENT_DATE_QC = 35
export const SIX_AND_SEVEN_YEAR_TERMS_MAX_INTEREST_RATE = 21.99

// Session tracking constants
export const SESSION_TRACKING_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours
export const SESSION_TRACKING_MAX_RETRY_ATTEMPTS = 3
export const SESSION_TRACKING_BASE_DELAY_MS = 1000 // 1 second

export const PARTNER_ID_MAPPING = {
    production: {
        FIG_FINANCIAL: '001Dn00000HrdDO',
        KIZZO: '001N300000zCz4f'
    },
    staging: {
        FIG_FINANCIAL: '001bZ000002ij3V',
        KIZZO: '001bZ00000M78dk'
    }
}
